package org.vreo.devicehub.features.liveAccess.presentation

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow

import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Cast
import androidx.compose.material.icons.filled.CastConnected
import androidx.compose.material.icons.filled.ErrorOutline
import androidx.compose.material.icons.filled.Link
import androidx.compose.material.icons.filled.LinkOff
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material.icons.filled.Videocam
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ElevatedCard
import androidx.compose.material3.FilledTonalButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.ListItem
import androidx.compose.material3.ListItemDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Surface
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle

import devicehub.composeapp.generated.resources.Res
import devicehub.composeapp.generated.resources.error_general_title
import devicehub.composeapp.generated.resources.live_view_connect_label
import devicehub.composeapp.generated.resources.live_view_disconnect_label
import devicehub.composeapp.generated.resources.live_view_enable_label
import devicehub.composeapp.generated.resources.live_view_screen_title
import devicehub.composeapp.generated.resources.live_view_web_rtc_mode_label
import devicehub.composeapp.generated.resources.live_view_web_rtc_mode_title

import org.vreo.devicehub.features.core.presentation.components.ErrorDialog
import org.vreo.devicehub.features.core.presentation.components.LoadingDialog
import org.vreo.devicehub.features.core.presentation.components.largeDropdownMenu.LargeDropdownMenu
import org.vreo.devicehub.features.liveAccess.domain.WebRtcConnectMode
import org.vreo.devicehub.features.liveAccess.presentation.components.VideoPlayer
import org.vreo.devicehub.features.liveAccess.presentation.components.toUiText

/**
 * Live view screen root
 *
 * @param viewModel view model for live view screen
 */
@Composable
fun LiveAccessScreenRoot(
    viewModel: LiveAccessViewModel = koinViewModel(),
) {
    val state: LiveAccessState by viewModel.state.collectAsStateWithLifecycle()

    // When composable is not shown, disable live view to reduce load on device and resume otherwise
    DisposableEffect(Unit) {
        if (state.connected) {
            viewModel.onAction(LiveAccessActions.OnEnableClicked(true))
        }
        onDispose {
            viewModel.onAction(LiveAccessActions.OnEnableClicked(false))
        }
    }

    LiveAccessView(
        state = state,
        frameFlow = viewModel.frame,
        onAction = { action -> viewModel.onAction(action) }
    )
}

/**
 * Live access view screen composable
 *
 * @param state view state of the screen
 * @param frameFlow read-only state flow of the frame bitmap
 * @param onAction action handler
 */
@Composable
fun LiveAccessView(
    state: LiveAccessState,
    frameFlow: StateFlow<ImageBitmap?>,
    onAction: (LiveAccessActions) -> Unit,
) {
    if (state.isLoading) LoadingDialog()
    state.error?.let { error ->
        ErrorDialog(
            dialogTitle = stringResource(Res.string.error_general_title),
            dialogText = error.asString(),
            onDismissRequest = { onAction(LiveAccessActions.OnClearError) },
            onConfirmation = { onAction(LiveAccessActions.OnClearError) }
        )
    }

    Column(
        modifier = Modifier
            .widthIn(max = 1800.dp)
            .padding(16.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        // Title row with connection status badge
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                Icon(
                    imageVector = if (state.connected) Icons.Default.CastConnected else Icons.Default.Cast,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(28.dp)
                )
                Text(
                    text = stringResource(Res.string.live_view_screen_title),
                    style = MaterialTheme.typography.headlineSmall,
                )
            }
            ConnectionStatusBadge(connected = state.connected)
        }

        // Device unavailable warning banner
        if (!state.isDeviceActive) {
            Surface(
                color = MaterialTheme.colorScheme.errorContainer,
                shape = MaterialTheme.shapes.small,
                modifier = Modifier.fillMaxWidth()
            ) {
                Row(
                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Icon(
                        imageVector = Icons.Default.ErrorOutline,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onErrorContainer
                    )
                    Text(
                        text = "Device is unavailable",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onErrorContainer
                    )
                }
            }
        }

        // Controls card
        OutlinedCard(
            modifier = Modifier.fillMaxWidth(),
            shape = MaterialTheme.shapes.medium
        ) {
            Column(modifier = Modifier.padding(vertical = 8.dp)) {

                // Stream enable toggle
                ListItem(
                    leadingContent = {
                        Icon(
                            imageVector = Icons.Default.Videocam,
                            contentDescription = null,
                            tint = if (state.connected) MaterialTheme.colorScheme.primary
                            else MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    },
                    headlineContent = {
                        Text(
                            text = stringResource(Res.string.live_view_enable_label),
                            style = MaterialTheme.typography.bodyLarge
                        )
                    },
                    supportingContent = if (!state.connected) {
                        {
                            Text(
                                text = "Connect first to enable stream",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    } else null,
                    trailingContent = {
                        Switch(
                            checked = state.liveViewEnabled,
                            onCheckedChange = { onAction(LiveAccessActions.OnEnableClicked(it)) },
                            enabled = state.connected
                        )
                    },
                    colors = ListItemDefaults.colors(containerColor = Color.Transparent)
                )

                HorizontalDivider(modifier = Modifier.padding(horizontal = 16.dp))

                // Connection mode section
                Column(
                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 14.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Icon(
                            imageVector = Icons.Default.Settings,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.size(18.dp)
                        )
                        Text(
                            text = stringResource(Res.string.live_view_web_rtc_mode_title),
                            style = MaterialTheme.typography.titleSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }

                    LargeDropdownMenu(
                        label = stringResource(Res.string.live_view_web_rtc_mode_label),
                        items = WebRtcConnectMode.entries.map { it.toUiText().asString() },
                        selectedIndex = state.connectionModeIndex,
                        onItemSelected = { onAction(LiveAccessActions.OnUpdateConnectionMode(it)) },
                    )
                }

                HorizontalDivider(modifier = Modifier.padding(horizontal = 16.dp))

                // Connect / Disconnect primary action
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 12.dp)
                ) {
                    if (state.connected) {
                        FilledTonalButton(
                            onClick = { onAction(LiveAccessActions.OnDisconnectClicked) },
                            modifier = Modifier.fillMaxWidth(),
                            contentPadding = PaddingValues(vertical = 14.dp, horizontal = 24.dp)
                        ) {
                            Icon(
                                imageVector = Icons.Default.LinkOff,
                                contentDescription = null,
                                modifier = Modifier.size(ButtonDefaults.IconSize)
                            )
                            Spacer(Modifier.width(ButtonDefaults.IconSpacing))
                            Text(text = stringResource(Res.string.live_view_disconnect_label))
                        }
                    } else {
                        Button(
                            enabled = state.isDeviceActive,
                            onClick = { onAction(LiveAccessActions.OnConnectClicked) },
                            modifier = Modifier.fillMaxWidth(),
                            contentPadding = PaddingValues(vertical = 14.dp, horizontal = 24.dp)
                        ) {
                            Icon(
                                imageVector = Icons.Default.Link,
                                contentDescription = null,
                                modifier = Modifier.size(ButtonDefaults.IconSize)
                            )
                            Spacer(Modifier.width(ButtonDefaults.IconSpacing))
                            Text(text = stringResource(Res.string.live_view_connect_label))
                        }
                    }
                }
            }
        }

        // Video feed
        ElevatedCard(
            shape = MaterialTheme.shapes.medium,
            elevation = CardDefaults.elevatedCardElevation(defaultElevation = 4.dp),
            modifier = Modifier.fillMaxWidth()
        ) {
            VideoPlayer(
                frameFlow = frameFlow,
                enable = state.liveViewEnabled
            )
        }
    }
}

@Composable
private fun ConnectionStatusBadge(connected: Boolean) {
    val backgroundColor by animateColorAsState(
        targetValue = if (connected) MaterialTheme.colorScheme.secondaryContainer
        else MaterialTheme.colorScheme.surfaceVariant,
        animationSpec = tween(durationMillis = 400),
        label = "status_bg"
    )
    val contentColor by animateColorAsState(
        targetValue = if (connected) MaterialTheme.colorScheme.onSecondaryContainer
        else MaterialTheme.colorScheme.onSurfaceVariant,
        animationSpec = tween(durationMillis = 400),
        label = "status_content"
    )
    Surface(
        color = backgroundColor,
        shape = MaterialTheme.shapes.small,
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            Icon(
                imageVector = if (connected) Icons.Default.CastConnected else Icons.Default.Cast,
                contentDescription = null,
                tint = contentColor,
                modifier = Modifier.size(16.dp)
            )
            Text(
                text = if (connected) "Connected" else "Disconnected",
                style = MaterialTheme.typography.labelMedium,
                color = contentColor
            )
        }
    }
}

@Preview(showBackground = true)
@Composable
fun LiveViewPreview() {
    MaterialTheme {
        LiveAccessView(
            state = LiveAccessState(),
            frameFlow = MutableStateFlow(null),
            onAction = {}
        )
    }
}
